import { clearClientIdTokenCache } from '@/utils/auth/getClientIdToken';
import { logLogoutActivity } from '@/utils/auth/logLogoutActivity';
import { removeSessionTimer } from '@/utils/auth/sessionLimitCookie';
import {
  clearCognitoTokenStore,
  signOutUser,
} from '@/utils/aws/cognito/amplifyAuth';
import { switchAuthFlowType } from '@/utils/aws/cognito/config';
import { resetAmplifyCookies } from '@/utils/aws/cognito/resetCookies';
import { IS_ETS_NEXT } from '@/utils/constants';
import { postHubApi } from '@/utils/hooks/api/typescript/methodHelpers';
import { isAxiosError } from 'axios';

interface HubAmplifySignOutInput {
  reason: string;
  userEmail?: string | null;
}

/**
 * Hub-only: POSTs to clear `hub_users.mfaLastSatisfiedAtUtc` for the current session.
 * No-ops on the server and in ETS Next / go.gov builds where MFA session cache does not apply.
 * Uses {@link postHubApi} (same route wiring as usePost) because hooks cannot run here.
 */
export async function clearMfaSessionSatisfaction(): Promise<void> {
  if (typeof window === 'undefined' || IS_ETS_NEXT) {
    return;
  }
  try {
    await postHubApi('/auth/clear-mfa-session-satisfaction', undefined, {
      timeout: 10_000,
    });
  } catch (err: unknown) {
    const status = isAxiosError(err) ? err.response?.status : undefined;
    if (status === 401) {
      return;
    }
    if (status) {
      console.warn('clearMfaSessionSatisfaction: unexpected status', status);
    }
    console.error('clearMfaSessionSatisfaction failed', err);
  }
}

/**
 * Clears Cognito tokens from the browser so the next sign-in runs a full CUSTOM_AUTH flow.
 * Also drops the in-memory ID token cache so a later sign-in cannot reuse the
 * previous user's JWT. Safe to call when already signed out (errors are ignored).
 */
export async function clearLocalCognitoSession(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    await signOutUser();
  } catch {
    // Not signed in, or Amplify sign-out failed — still wipe tokens/cookies below.
  } finally {
    clearClientIdTokenCache();
    await clearCognitoTokenStore();
    clearClientIdTokenCache();
    resetAmplifyCookies();
    switchAuthFlowType('CUSTOM_AUTH');
    removeSessionTimer();
  }
}

/**
 * Hub builds: logs the logout, clears MFA last-satisfied state on the server, then Cognito sign-out.
 * Call sites add navigation / impersonation after this as needed.
 */
export async function hubAmplifySignOut({
  reason,
  userEmail,
}: HubAmplifySignOutInput): Promise<void> {
  await logLogoutActivity({ reason, userEmail });
  await clearMfaSessionSatisfaction();
  await clearLocalCognitoSession();
}
